// src/app/common/validators/phone-au.validator.ts
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Constants for Australian phone prefixes
const codeAU = '61';
const phoneCodeAU = '+' + codeAU;

// Australian phone number types and their patterns
const PHONE_TYPES = {
  MOBILE: 'mobile', // 04XX XXX XXX - 10 digits
  LANDLINE: 'landline', // 0X XXXX XXXX - 10 digits (X = 2,3,7,8)
  SERVICE_13: '13', // 13X XXX - 6 digits
  SERVICE_1300: '1300', // 1300 XXX XXX - 10 digits
  SERVICE_18: '18', // 18X XXX - 6 digits
  SERVICE_1800: '1800', // 1800 XXX XXX - 10 digits
  OTHER: 'other', // Any other valid format
  INVALID: 'invalid', // Invalid format
};

/**
 * Normalizes an Australian phone number by removing formatting characters
 * and converting international format to local format.
 *
 * @param phone The phone number to normalize
 * @returns Normalized phone number string
 */
export function normalizePhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';

  // Remove spaces, dashes, parentheses, and other formatting characters
  let normalized = phone.replace(/\s+|-|\(|\)|\./g, '');

  // Handle international format for Australian numbers only
  if (normalized.startsWith('+' + codeAU)) {
    // Replace +61 with 0
    normalized = '0' + normalized.substring(3);
  } else if (normalized.startsWith(codeAU) && normalized.length >= 9) {
    // Replace 61 with 0
    normalized = '0' + normalized.substring(2);
  }

  return normalized;
}

/**
 * Normalizes international phone numbers (preserves + and country code)
 */
function normalizeInternationalPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';

  // For international numbers, only remove formatting but keep the + and digits
  return phone.replace(/[^\d+]/g, '');
}

/**
 * Formats an Australian phone number with appropriate spaces based on its type
 *
 * @param phone The phone number to format
 * @returns Formatted phone number with spaces
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';

  // Handle international numbers starting with +
  if (phone.startsWith('+')) {
    return formatInternationalPhoneNumber(phone);
  }

  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return '';

  const phoneType = getPhoneNumberType(normalized);

  // Format based on phone type
  switch (phoneType) {
    case PHONE_TYPES.MOBILE:
      // Format: 04XX XXX XXX
      if (normalized.length < 4) return normalized;
      return `${normalized.substring(0, 4)}${
        normalized.length > 4 ? ' ' + normalized.substring(4, Math.min(7, normalized.length)) : ''
      }${normalized.length > 7 ? ' ' + normalized.substring(7) : ''}`;

    case PHONE_TYPES.LANDLINE:
      // Format: 0X XXXX XXXX
      if (normalized.length < 2) return normalized;
      return `${normalized.substring(0, 2)}${
        normalized.length > 2 ? ' ' + normalized.substring(2, Math.min(6, normalized.length)) : ''
      }${normalized.length > 6 ? ' ' + normalized.substring(6) : ''}`;

    case PHONE_TYPES.SERVICE_13:
    case PHONE_TYPES.SERVICE_18:
      // Updated format: 1XX XXX (e.g. 131 000, 132 500, 180 000)
      if (normalized.length < 3) return normalized;
      return `${normalized.substring(0, 3)}${normalized.length > 3 ? ' ' + normalized.substring(3) : ''}`;

    case PHONE_TYPES.SERVICE_1300:
    case PHONE_TYPES.SERVICE_1800:
      // Format: 1X00 XXX XXX
      if (normalized.length < 4) return normalized;
      return `${normalized.substring(0, 4)}${
        normalized.length > 4 ? ' ' + normalized.substring(4, Math.min(7, normalized.length)) : ''
      }${normalized.length > 7 ? ' ' + normalized.substring(7) : ''}`;

    default:
      // For partial input or other formats, try a reasonable default formatting
      if (normalized.length <= 3) {
        return normalized;
      } else if (normalized.length <= 6) {
        return `${normalized.substring(0, 3)} ${normalized.substring(3)}`;
      } else if (normalized.length <= 10) {
        return `${normalized.substring(0, 4)} ${normalized.substring(4, 7)} ${normalized.substring(7)}`;
      } else {
        return normalized; // For longer numbers, just return as is
      }
  }
}

/**
 * Formats international phone numbers with proper spacing
 */
function formatInternationalPhoneNumber(phone: string): string {
  if (!phone.startsWith('+')) return phone;

  // Remove any existing formatting but keep the +
  const cleanNumber = phone.replace(/[^\d+]/g, '');

  if (cleanNumber.length <= 1) return cleanNumber; // Just "+"

  // Extract country code and number parts
  const withoutPlus = cleanNumber.substring(1);

  // Handle Australian numbers (+61) - convert to local format
  if (withoutPlus.length >= 2 && withoutPlus.substring(0, 2) === '61' && withoutPlus.length > 2) {
    const localNumber = '0' + withoutPlus.substring(2);
    return formatPhoneNumber(localNumber);
  }

  // For other countries, apply simple international formatting: +XX followed by digits
  // Common country code lengths: 1-3 digits
  let countryCodeLength = 1;
  if (withoutPlus.length >= 2) {
    // Common 2-digit country codes
    const twoDigitCode = withoutPlus.substring(0, 2);
    if (
      [
        '20',
        '27',
        '30',
        '31',
        '32',
        '33',
        '34',
        '36',
        '39',
        '40',
        '41',
        '43',
        '44',
        '45',
        '46',
        '47',
        '48',
        '49',
        '51',
        '52',
        '53',
        '54',
        '55',
        '56',
        '57',
        '58',
        '60',
        '62',
        '63',
        '64',
        '65',
        '66',
        '81',
        '82',
        '84',
        '86',
        '90',
        '91',
        '92',
        '93',
        '94',
        '95',
        '98',
      ].includes(twoDigitCode)
    ) {
      countryCodeLength = 2;
    } else if (withoutPlus.length >= 3) {
      // Common 3-digit country codes
      const threeDigitCode = withoutPlus.substring(0, 3);
      if (
        [
          '212',
          '213',
          '216',
          '218',
          '220',
          '221',
          '222',
          '223',
          '224',
          '225',
          '226',
          '227',
          '228',
          '229',
          '230',
          '231',
          '232',
          '233',
          '234',
          '235',
          '236',
          '237',
          '238',
          '239',
          '240',
          '241',
          '242',
          '243',
          '244',
          '245',
          '246',
          '248',
          '249',
          '250',
          '251',
          '252',
          '253',
          '254',
          '255',
          '256',
          '257',
          '258',
          '260',
          '261',
          '262',
          '263',
          '264',
          '265',
          '266',
          '267',
          '268',
          '269',
          '290',
          '291',
          '297',
          '298',
          '299',
          '350',
          '351',
          '352',
          '353',
          '354',
          '355',
          '356',
          '357',
          '358',
          '359',
          '370',
          '371',
          '372',
          '373',
          '374',
          '375',
          '376',
          '377',
          '378',
          '380',
          '381',
          '382',
          '383',
          '385',
          '386',
          '387',
          '389',
          '420',
          '421',
          '423',
          '500',
          '501',
          '502',
          '503',
          '504',
          '505',
          '506',
          '507',
          '508',
          '509',
          '590',
          '591',
          '592',
          '593',
          '594',
          '595',
          '596',
          '597',
          '598',
          '599',
          '670',
          '672',
          '673',
          '674',
          '675',
          '676',
          '677',
          '678',
          '679',
          '680',
          '681',
          '682',
          '683',
          '684',
          '685',
          '686',
          '687',
          '688',
          '689',
          '690',
          '691',
          '692',
          '850',
          '852',
          '853',
          '855',
          '856',
          '880',
          '886',
          '960',
          '961',
          '962',
          '963',
          '964',
          '965',
          '966',
          '967',
          '968',
          '970',
          '971',
          '972',
          '973',
          '974',
          '975',
          '976',
          '977',
          '992',
          '993',
          '994',
          '995',
          '996',
          '998',
        ].includes(threeDigitCode)
      ) {
        countryCodeLength = 3;
      }
    }
  }

  const countryCode = withoutPlus.substring(0, countryCodeLength);
  const remainingNumber = withoutPlus.substring(countryCodeLength);

  if (remainingNumber.length === 0) {
    return `+${countryCode}`;
  }

  // Simple format: +XX followed by all remaining digits
  return `+${countryCode} ${remainingNumber}`;
}

/**
 * Handles partial input formatting during typing
 *
 * @param phone The phone number being typed
 * @returns Formatted partial phone number
 */
export function formatPartialPhoneNumber(phone: string): string {
  if (!phone) return '';

  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return '';

  // Try to detect the number type from the starting digits
  if (normalized.startsWith('04')) {
    // Looks like a mobile number
    if (normalized.length <= 4) {
      return normalized;
    } else if (normalized.length <= 7) {
      return `${normalized.substring(0, 4)} ${normalized.substring(4)}`;
    } else {
      return `${normalized.substring(0, 4)} ${normalized.substring(4, 7)} ${normalized.substring(
        7,
        Math.min(10, normalized.length),
      )}`;
    }
  } else if (/^0[2378]/.test(normalized)) {
    // Looks like a landline
    if (normalized.length <= 2) {
      return normalized;
    } else if (normalized.length <= 6) {
      return `${normalized.substring(0, 2)} ${normalized.substring(2)}`;
    } else {
      return `${normalized.substring(0, 2)} ${normalized.substring(2, 6)} ${normalized.substring(
        6,
        Math.min(10, normalized.length),
      )}`;
    }
  } else if (normalized.startsWith('13') || normalized.startsWith('18')) {
    // Looks like a 13/18 service number
    if (normalized.length <= 3) {
      return normalized;
    } else {
      return `${normalized.substring(0, 3)} ${normalized.substring(3, Math.min(6, normalized.length))}`;
    }
  } else if (normalized.startsWith('1300') || normalized.startsWith('1800')) {
    // Looks like a 1300/1800 service number
    if (normalized.length <= 4) {
      return normalized;
    } else if (normalized.length <= 7) {
      return `${normalized.substring(0, 4)} ${normalized.substring(4)}`;
    } else {
      return `${normalized.substring(0, 4)} ${normalized.substring(4, 7)} ${normalized.substring(
        7,
        Math.min(10, normalized.length),
      )}`;
    }
  }

  // Default formatting for unknown patterns
  if (normalized.length <= 4) {
    return normalized;
  } else if (normalized.length <= 7) {
    return `${normalized.substring(0, 4)} ${normalized.substring(4)}`;
  } else {
    return `${normalized.substring(0, 4)} ${normalized.substring(4, 7)} ${normalized.substring(7)}`;
  }
}

/**
 * Identifies the type of Australian phone number
 *
 * @param phone The phone number to check
 * @returns The type of phone number
 */
export function getPhoneNumberType(phone: string | null | undefined): string {
  if (!phone) return PHONE_TYPES.INVALID;

  const normalized = normalizePhoneNumber(phone);

  // Mobile numbers (04xx)
  if (normalized.startsWith('04') && normalized.length === 10) {
    return PHONE_TYPES.MOBILE;
  }

  // Landline numbers (02, 03, 07, 08)
  if (/^0[2378]/.test(normalized) && normalized.length === 10) {
    return PHONE_TYPES.LANDLINE;
  }

  // 13xxxx numbers (6 digits)
  if (normalized.startsWith('13') && normalized.length === 6) {
    return PHONE_TYPES.SERVICE_13;
  }

  // 1300 numbers (10 digits)
  if (normalized.startsWith('1300') && normalized.length === 10) {
    return PHONE_TYPES.SERVICE_1300;
  }

  // 18xxxx numbers (6 digits)
  if (normalized.startsWith('18') && normalized.length === 6) {
    return PHONE_TYPES.SERVICE_18;
  }

  // 1800 numbers (10 digits)
  if (normalized.startsWith('1800') && normalized.length === 10) {
    return PHONE_TYPES.SERVICE_1800;
  }

  // If it's the right length but doesn't match specific patterns
  if (normalized.length === 10 || normalized.length === 6) {
    return PHONE_TYPES.OTHER;
  }

  return PHONE_TYPES.INVALID;
}

/**
 * Checks if a phone number is a valid Australian mobile number
 *
 * @param phone The phone number to check
 * @returns True if the number is a valid Australian mobile
 */
export function isAustralianMobile(phone: string | null | undefined): boolean {
  return getPhoneNumberType(phone) === PHONE_TYPES.MOBILE;
}

/**
 * Checks if a phone number is any valid Australian number
 *
 * @param phone The phone number to check
 * @returns True if the number is any valid Australian number
 */
export function isValidAustralianNumber(phone: string | null | undefined): boolean {
  return getPhoneNumberType(phone) !== PHONE_TYPES.INVALID;
}

/**
 * Checks if a phone number is a valid non-mobile Australian number
 *
 * @param phone The phone number to check
 * @returns True if the number is a valid non-mobile Australian number
 */
export function isNonMobileAustralianNumber(phone: string | null | undefined): boolean {
  const type = getPhoneNumberType(phone);
  return type !== PHONE_TYPES.INVALID && type !== PHONE_TYPES.MOBILE;
}

/**
 * Converts a phone number to international format (+61)
 *
 * @param phone The phone number to normalize
 * @returns Phone number in international format
 */
export function normalizePhoneAU(phone: string | null | undefined): string {
  if (!phone) return '';

  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return '';

  if (normalized.startsWith('0')) {
    return phoneCodeAU + normalized.substring(1);
  }

  // 1300 and 1800 numbers are special Australian service numbers that don't get +61 prefix
  if (normalized.startsWith('13') || normalized.startsWith('18')) {
    return normalized;
  }

  return phoneCodeAU + normalized;
}

/**
 * Converts a phone number from international to local format
 *
 * @param phone The phone number to convert
 * @returns Phone number in local format (starting with 0)
 */
export function toPhoneAU(phone: string | null | undefined): string {
  if (!phone) return '';

  const normalized = normalizePhoneNumber(phone);
  return normalized;
}

/**
 * Validator for Australian phone numbers
 * Ensures the number is a valid Australian format
 */
export function PhoneAUValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;

  // Check if it's an international number starting with +
  if (control.value.startsWith('+')) {
    return validateInternationalPhoneNumber(control.value);
  }

  const normalized = normalizePhoneNumber(control.value);
  const phoneType = getPhoneNumberType(normalized);

  if (phoneType === PHONE_TYPES.INVALID) {
    // Check if this could be a landline number missing the area code
    // Australian landline numbers without area codes are typically 8 digits
    // and start with digits 2-9 (common first digits after area code)
    if (normalized.length === 8 && /^[2-9]/.test(normalized)) {
      // This looks like it might be missing an area code
      return { phone: { requiredLength: 10 } };
    }

    // Determine what kind of error to return
    if (!/^0/.test(normalized) && !/^13/.test(normalized) && !/^18/.test(normalized)) {
      return { countryCode: true };
    }

    // Determine required length for error message
    const requiredLength = getRequiredLength(normalized);
    return { phone: { requiredLength } };
  }

  return null;
}

/**
 * Validates international phone numbers
 */
function validateInternationalPhoneNumber(phone: string): ValidationErrors | null {
  // Remove any formatting but keep the +
  const cleanNumber = phone.replace(/[^\d+]/g, '');

  // Must start with + and have at least country code
  if (!cleanNumber.startsWith('+') || cleanNumber.length < 2) {
    return { countryCode: true };
  }

  const withoutPlus = cleanNumber.substring(1);

  // Country code should be 1-3 digits
  if (withoutPlus.length < 1) {
    return { countryCode: true };
  }

  // Check minimum total length (country code + number)
  if (cleanNumber.length < 8) {
    // At least +X and 6 digits
    return { phone: { requiredLength: 8 } };
  }

  // Check maximum total length (ITU-T standard is 15 digits max)
  if (cleanNumber.length > 15) {
    return { phone: { requiredLength: 15 } };
  }

  // Additional validation: ensure we have a valid country code pattern
  let countryCodeLength = 1;
  if (withoutPlus.length >= 2) {
    const twoDigitCode = withoutPlus.substring(0, 2);
    if (
      [
        '20',
        '27',
        '30',
        '31',
        '32',
        '33',
        '34',
        '36',
        '39',
        '40',
        '41',
        '43',
        '44',
        '45',
        '46',
        '47',
        '48',
        '49',
        '51',
        '52',
        '53',
        '54',
        '55',
        '56',
        '57',
        '58',
        '60',
        '61',
        '62',
        '63',
        '64',
        '65',
        '66',
        '81',
        '82',
        '84',
        '86',
        '90',
        '91',
        '92',
        '93',
        '94',
        '95',
        '98',
      ].includes(twoDigitCode)
    ) {
      countryCodeLength = 2;
    } else if (withoutPlus.length >= 3) {
      const threeDigitCode = withoutPlus.substring(0, 3);
      if (
        [
          '212',
          '213',
          '216',
          '218',
          '220',
          '221',
          '222',
          '223',
          '224',
          '225',
          '226',
          '227',
          '228',
          '229',
          '230',
          '231',
          '232',
          '233',
          '234',
          '235',
          '236',
          '237',
          '238',
          '239',
          '240',
          '241',
          '242',
          '243',
          '244',
          '245',
          '246',
          '248',
          '249',
          '250',
          '251',
          '252',
          '253',
          '254',
          '255',
          '256',
          '257',
          '258',
          '260',
          '261',
          '262',
          '263',
          '264',
          '265',
          '266',
          '267',
          '268',
          '269',
          '290',
          '291',
          '297',
          '298',
          '299',
          '350',
          '351',
          '352',
          '353',
          '354',
          '355',
          '356',
          '357',
          '358',
          '359',
          '370',
          '371',
          '372',
          '373',
          '374',
          '375',
          '376',
          '377',
          '378',
          '380',
          '381',
          '382',
          '383',
          '385',
          '386',
          '387',
          '389',
          '420',
          '421',
          '423',
          '500',
          '501',
          '502',
          '503',
          '504',
          '505',
          '506',
          '507',
          '508',
          '509',
          '590',
          '591',
          '592',
          '593',
          '594',
          '595',
          '596',
          '597',
          '598',
          '599',
          '670',
          '672',
          '673',
          '674',
          '675',
          '676',
          '677',
          '678',
          '679',
          '680',
          '681',
          '682',
          '683',
          '684',
          '685',
          '686',
          '687',
          '688',
          '689',
          '690',
          '691',
          '692',
          '850',
          '852',
          '853',
          '855',
          '856',
          '880',
          '886',
          '960',
          '961',
          '962',
          '963',
          '964',
          '965',
          '966',
          '967',
          '968',
          '970',
          '971',
          '972',
          '973',
          '974',
          '975',
          '976',
          '977',
          '992',
          '993',
          '994',
          '995',
          '996',
          '998',
        ].includes(threeDigitCode)
      ) {
        countryCodeLength = 3;
      }
    }
  }

  // Check if we have enough digits after the country code
  const remainingDigits = withoutPlus.substring(countryCodeLength);
  if (remainingDigits.length < 4) {
    // Minimum 4 digits after country code
    return { phone: { requiredLength: countryCodeLength + 4 + 1 } }; // +1 for the +
  }

  return null; // Valid international number
}

/**
 * Validator that specifically requires an Australian mobile number
 */
export function MobileAUValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;

  if (!isAustralianMobile(control.value)) {
    return { notMobile: true };
  }

  return null;
}

/**
 * Conditional validator that requires either:
 * 1. A valid mobile number, OR
 * 2. Any other valid number + email
 */
export function conditionalContactValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const email = formGroup.get('email')?.value;
    const phone = formGroup.get('phone')?.value;

    // Case 1: No contact method provided
    if (!phone && !email) {
      return { noContactMethod: true };
    }

    // Case 2: Email is provided - phone becomes optional
    if (email) {
      // If phone is also provided, validate its format
      if (phone) {
        // Check if it's a valid Australian number
        if (!isValidAustralianNumber(phone)) {
          // If not Australian, check if it's a valid international number
          if (!phone.startsWith('+') || validateInternationalPhoneNumber(phone) !== null) {
            return { invalidPhone: true };
          }
        }
      }

      // Valid email provided, phone is either valid or not provided
      return null;
    }

    // Case 3: No email, but phone is provided
    if (phone) {
      // Is it a mobile?
      if (isAustralianMobile(phone)) {
        // Mobile number is sufficient
        return null;
      }

      // Is it any other valid Australian number?
      if (isNonMobileAustralianNumber(phone)) {
        // Non-mobile numbers require email
        return { nonMobileRequiresEmail: true };
      }

      // Check if it's a valid international number
      if (phone.startsWith('+') && validateInternationalPhoneNumber(phone) === null) {
        // International numbers require email
        return { internationalRequiresEmail: true };
      }

      // Invalid phone format
      return { invalidPhone: true };
    }

    return null; // All validations pass
  };
}

/**
 * Helper function to determine the required length for error messages
 */
function getRequiredLength(value: string): number {
  if (
    value.startsWith('04') ||
    value.startsWith('02') ||
    value.startsWith('03') ||
    value.startsWith('07') ||
    value.startsWith('08') ||
    value.startsWith('1300') ||
    value.startsWith('1800')
  ) {
    return 10;
  } else if (value.startsWith('13') || value.startsWith('18')) {
    return 6;
  } else {
    return 10; // Default expected length
  }
}
